// appConfigSlice.js
import { createSlice } from "@reduxjs/toolkit";

const appConfigSlice = createSlice({
  name: "appConfig",
  initialState: {
    // --- UI Visibility & Mode Flags ---
    isPlaybackMode: false,
    isSidebarVisible: false,
    isLargeWindowSidebarVisible: true,

    // --- Display & User Interface ---
    brightness: {
      value: 1,
      auto: false,
    },
    importedIcons: [],
    lockState: {},

    // --- Live Data & Communication ---
    ledLive: false,
    wsRequestData: {
      tagsSources: {},
      operationSources: {},
      msIntInterval: 1000,
    },

    // --- Ship & PLC Configuration ---
    shipSerials: [],
    plcList: [],
    envSerial: "",

    // --- Page Access Flags (derived from per-user deny permissions) ---
    canViewSensorsConfig: true,
    canViewPlcConfig: true,

    // --- Alarms & Dashboards ---
    alarms_plc_config: false,
    alarms_acknowledge_enabled: false,
    dashboards: {},
    dashboardNames: [],
    currentDashboard: null,
  },
  reducers: {
    setPlaybackMode: (state, action) => {
      state.isPlaybackMode = action.payload;
    },
    setSidebarVisible: (state, action) => {
      state.isSidebarVisible = action.payload;
    },
    setLargeWindowSidebarVisible: (state, action) => {
      state.isLargeWindowSidebarVisible = action.payload;
    },
    setLedLive: (state, action) => {
      state.ledLive = action.payload;
    },
    setShipSerials: (state, action) => {
      state.shipSerials = action.payload;
    },

    setWsRequestData: (state, action) => {
      state.wsRequestData = { ...state.wsRequestData, ...action.payload };
    },

    /* Per alarm table: { [tableKey]: { [ship]: [tag, ...] } } for ACTIVE / LIVE alarm tags */
    setAlarmLiveSources: (state, action) => {
      const { tableKey, sources } = action.payload;
      state.wsRequestData.alarmLiveSources = {
        ...state.wsRequestData.alarmLiveSources,
        [tableKey]: sources,
      };
    },
    removeAlarmLiveSources: (state, action) => {
      if (!state.wsRequestData.alarmLiveSources) return;
      delete state.wsRequestData.alarmLiveSources[action.payload];
    },

    setPLCList: (state, action) => {
      state.plcList = action.payload;
    },
    setAlarmsPlcConfig: (state, action) => {
      state.alarms_plc_config = action.payload;
    },
    setAlarmsAcknowledgeEnabled: (state, action) => {
      state.alarms_acknowledge_enabled = action.payload;
    },
    setCanViewSensorsConfig: (state, action) => {
      state.canViewSensorsConfig = action.payload;
    },
    setCanViewPlcConfig: (state, action) => {
      state.canViewPlcConfig = action.payload;
    },
    addNeededSensors: (state, action) => {
      // αν δεν υπάρχει tagsSources, το αρχικοποιούμε σε κενό αντικείμενο
      if (!state.wsRequestData.tagsSources) {
        state.wsRequestData.tagsSources = {};
      }

      // τώρα προσθέτουμε κανονικά
      Object.entries(action.payload).forEach(([ship, sensors]) => {
        const existing = state.wsRequestData.tagsSources[ship] || [];
        state.wsRequestData.tagsSources[ship] = [...new Set([...existing, ...sensors])];
      });
    },

    setLockState: (state, action) => {
      state.lockState = action.payload;
    },
    setEnvSerial: (state, action) => {
      state.envSerial = action.payload;
    },
    setImportedIcons: (state, action) => {
      state.importedIcons = action.payload;
    },
    addImportedIcons: (state, action) => {
      state.importedIcons = [...state.importedIcons, ...action.payload];
    },
    setBrightnessValue: (state, action) => {
      state.brightness.value = action.payload;
    },
    setBrightnessAuto: (state, action) => {
      state.brightness.auto = action.payload;
    },
    setBrightness: (state, action) => {
      state.brightness = action.payload;
    },
    removeImportedIcon: (state, action) => {
      state.importedIcons = state.importedIcons.filter((icon) => icon.filename !== action.payload);
    },
    setAllDashboards(state, action) {
      // action.payload should be something like { dashboards: { … }, activeDashboardName: "…" }
      state.dashboards = action.payload.dashboards || {};
      state.currentDashboard = action.payload.activeDashboardName || state.currentDashboard;
    },

    // Update (or add) a single dashboard’s elements & layout & version
    upsertDashboard(state, action) {
      // payload = { name: string, elements: [], layout: [], version: string (optional) }
      const { name, elements, layout, version } = action.payload;
      if (!state.dashboards[name]) {
        state.dashboards[name] = { elements: [], layout: [], version: "" };
      }
      state.dashboards[name].elements = elements;
      state.dashboards[name].layout = layout;
      if (version !== undefined) {
        state.dashboards[name].version = version;
      }
      // make this the “active” dashboard if desired
      state.currentDashboard = name;
    },

    // Rename a dashboard key (oldName → newName)
    renameDashboard(state, action) {
      // payload = { oldName: string, newName: string }
      const { oldName, newName } = action.payload;
      if (state.dashboards[oldName] && !state.dashboards[newName]) {
        state.dashboards[newName] = state.dashboards[oldName];
        delete state.dashboards[oldName];

        // If the active was oldName, switch to newName
        if (state.currentDashboard === oldName) {
          state.currentDashboard = newName;
        }
      }
    },

    // Remove a dashboard entirely
    removeDashboard(state, action) {
      // payload = { name: string }
      const name = action.payload.name;
      if (state.dashboards[name]) {
        delete state.dashboards[name];
        // If we removed the currently active, pick another (or fallback)
        if (state.currentDashboard === name) {
          const keys = Object.keys(state.dashboards);
          state.currentDashboard = keys.length ? keys[0] : "";
        }
      }
    },

    setCurrentDashboard(state, action) {
      state.currentDashboard = action.payload;
    },
    setDashboardNames(state, action) {
      state.dashboardNames = action.payload; // string[]
    },
  },
});

export const {
  setSidebarVisible,
  setLargeWindowSidebarVisible,
  setLedLive,
  setShipSerials,
  setWsRequestData,
  setAlarmLiveSources,
  removeAlarmLiveSources,
  setOperationSources,
  addNeededSensors,
  setImportedIcons,
  addImportedIcons,
  setLockState,
  removeImportedIcon,
  setPlaybackMode,
  setEnvSerial,
  setPLCList,
  setBrightnessValue,
  setBrightnessAuto,
  setBrightness,
  setAlarmsPlcConfig,
  setAlarmsAcknowledgeEnabled,
  setCanViewSensorsConfig,
  setCanViewPlcConfig,
  setAllDashboards,
  upsertDashboard,
  renameDashboard,
  removeDashboard,
  setCurrentDashboard,
  setDashboardNames,
} = appConfigSlice.actions;

export default appConfigSlice.reducer;
